import type {
  CustomChart,
  CustomChartSerie,
} from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { dequal } from "dequal"
import { Fragment, memo, Suspense, useContext, useMemo, useState } from "react"
import { Button } from "../../../../ui/button/Button"
import { Divider } from "../../../../ui/layout/divider/Divider"
import { MenuButton, MenuItem } from "../../../../ui/menu/MenuButton"
import { ChartContext } from "../../tt-ui-chart/ChartContext"
import type { ChartSerie } from "../../tt-ui-chart/useHighchartOptions"
import { dimSerie, undimSerie } from "../../tt-ui-chart/utils/legendDim"
import { getSeriesColor } from "../../utils/colors"
import { LegendItem } from "./LegendItem"
import { LegendPlaceholder } from "./Placeholder"

type LegendProps = {
  onVisibilityChange?: (id: string, isVisible: boolean) => void
  chartSerieSettings: CustomChart
  series: ChartSerie[]
}

const DISPLAY_LIMIT = 20

// The server labels the remainder band exactly "Other" (see the sector-chart
// "Other" series in services/api). Matched case-sensitively: the label is the
// sentinel this whole package keys on (colors, palette offsets), and a looser
// match could swallow a real series that happens to be named "other".
const OTHER_LABEL = "Other"

/**
 * The drawn stack slots the "Other" remainder band by size (a server-side
 * rule — it can land anywhere, including first), but the legend always lists
 * "Other" last: it's the catch-all row, not a ranked entry. Relative order of
 * everything else is preserved.
 *
 * Returned split (not one array) so the display cap can apply to ranked
 * entries only: "Other" must stay inline even at the cap boundary — the
 * copy-as-image capture never opens the "+ N more" menu, so a capped Other
 * would silently vanish from the exported image.
 */
export function splitOtherLast(series: ChartSerie[]): {
  ranked: ChartSerie[]
  others: ChartSerie[]
} {
  return {
    ranked: series.filter((serie) => getLabelForSerie(serie) !== OTHER_LABEL),
    others: series.filter((serie) => getLabelForSerie(serie) === OTHER_LABEL),
  }
}

function LegendRow({
  settings,
  onVisbilityChange,
  offsetCount,
  series,
}: {
  settings: CustomChartSerie
  onVisbilityChange?: (
    serieId: string,
    visibility: boolean,
    legendItemIds?: string[]
  ) => void
  offsetCount: number
  series: ChartSerie[]
}) {
  const [, rerender] = useState(0)
  const { chartApi } = useContext(ChartContext)

  const safeChartGet = (name: string): Highcharts.Series | null => {
    try {
      return chartApi?.get(name) as Highcharts.Series | null
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      /* empty */
      return null
    }
  }

  const { ranked, others } = splitOtherLast(
    series.filter((serie) => serie.id === settings.id)
  )
  const chartInfo = [...ranked, ...others]

  // Series names are `${serieId}:${value}`. The value itself can contain ":"
  // for composite group-bys (e.g. "market sector & venue" =
  // `${serieId}:${market_sector_id}:${venue_data_id}:${venue_product_id}`), so
  // take everything after the FIRST colon rather than only the last segment.
  // This keeps the value in sync with ChartPreview's `${serieId}:${value}`
  // reconstruction; using `.split(":").pop()` dropped all but the last segment
  // and broke per-series toggling for venue composites (DEV-5147).
  const allLegendItemIds = chartInfo.map((item) =>
    item.name.slice(item.name.indexOf(":") + 1)
  )

  // The cap counts ranked entries only; "Other" always renders inline last.
  // Folding it into "+ N more" would drop it from copy-as-image captures,
  // which can't open that menu.
  const legendItems = [...ranked.slice(0, DISPLAY_LIMIT), ...others]
  const moreItems = ranked.slice(DISPLAY_LIMIT, Infinity)

  return (
    <div className="flex flex-col gap-1.5 max-w-full">
      <div className="text-xs text-fg-secondary whitespace-nowrap">
        {settings.title}
      </div>
      <div className="flex gap-x-4 gap-y-2 items-center justify-start flex-wrap">
        {legendItems.map((info, idx) => {
          const globalGroupIndex = offsetCount + idx
          const id = info.name

          const label = settings.groupBy
            ? (info.info?.legendLabel ?? info.name)
            : "Aggregated"

          // Prefer the color stamped on the series itself so the legend always
          // matches the drawn chart, even when series were re-sorted after
          // colors were assigned (static bar charts stack by drawn size but
          // keep palette colors keyed to the latest-value ranking).
          const currentColor =
            info.color ??
            getSeriesColor({
              colorsConfig: settings.colors,
              groupId: label === OTHER_LABEL ? OTHER_LABEL : id,
              groupIndex: idx,
              globalGroupIndex,
              resolvePaletteColors: false,
            })

          let serie = null

          // `info`, not `chartInfo[idx]`: with "Other" pulled inline past the
          // ranked cap, legend position no longer equals chartInfo position.
          if (chartApi && info.name && !onVisbilityChange) {
            serie = safeChartGet(`serie-${info.name}`)
          }

          const isVisible = onVisbilityChange
            ? settings.visible && settings.visible?.length > 0
              ? (settings.visible?.includes?.(id) ?? true)
              : true
            : (serie?.visible ?? true)

          return (
            <LegendItem
              key={`${id}-${idx}`}
              name={label}
              color={currentColor}
              serieType={settings.chart_type}
              isActive={isVisible}
              onMouseOver={() => {
                const currentSerie = safeChartGet(`serie-${id}`)

                // if serie is not visible, we don't trigger this action
                if (!currentSerie?.visible) {
                  return
                }

                chartInfo.forEach((info) => {
                  const serie = safeChartGet(`serie-${info.name}`)
                  if (serie && info.name !== id) {
                    dimSerie(serie)
                  }
                })
              }}
              onMouseOut={() => {
                chartInfo.forEach((info) => {
                  const serie = safeChartGet(`serie-${info.name}`)
                  if (serie && info.name !== id) {
                    undimSerie(serie)
                  }
                })
              }}
              onClick={() => {
                const filteredLegendItemIds = allLegendItemIds.filter(
                  (itemId): itemId is string => itemId !== undefined
                )

                onVisbilityChange?.(id, !isVisible, filteredLegendItemIds)

                if (!onVisbilityChange) {
                  const serie = safeChartGet(`serie-${info.name}`)
                  if (serie) {
                    serie.setVisible(!serie.visible, true)
                    rerender((prev) => prev + 1)
                  }
                }
              }}
            />
          )
        })}
        {moreItems.length > 0 && (
          <MenuButton
            trigger={
              <Button size="xsmall" variant="ghost" className="text-xs">
                + {moreItems.length} more
              </Button>
            }
          >
            {moreItems.map((info, idx) => {
              const globalGroupIndex = offsetCount + idx + DISPLAY_LIMIT
              const id = info.name
              const label = settings.groupBy
                ? (info.info?.legendLabel ?? info.name)
                : "Aggregated"

              // Same as above: the stamped series color is the source of truth
              const currentColor =
                info.color ??
                getSeriesColor({
                  colorsConfig: settings.colors,
                  groupId: label === OTHER_LABEL ? OTHER_LABEL : id,
                  groupIndex: idx,
                  globalGroupIndex,
                  resolvePaletteColors: false,
                })

              const isVisible = onVisbilityChange
                ? settings.visible && settings.visible?.length > 0
                  ? (settings.visible?.includes?.(id) ?? true)
                  : true
                : true

              return (
                <MenuItem
                  key={id}
                  onHoverStart={() => {
                    const currentSerie = safeChartGet(`serie-${id}`)

                    // if serie is not visible, we don't trigger this action
                    if (!currentSerie?.visible) {
                      return
                    }

                    chartInfo.forEach((info) => {
                      const serie = safeChartGet(`serie-${info.name}`)
                      if (serie && info.name !== id) {
                        dimSerie(serie)
                      }
                    })
                  }}
                  onHoverEnd={() => {
                    chartInfo.forEach((info) => {
                      const serie = safeChartGet(`serie-${info.name}`)
                      if (serie && info.name !== id) {
                        undimSerie(serie)
                      }
                    })
                  }}
                  onAction={() => {
                    // `info`, not `chartInfo[idx]`: idx is the position within
                    // moreItems, which never matched chartInfo's indexing here.
                    const filteredLegendItemIds = allLegendItemIds.filter(
                      (itemId): itemId is string => itemId !== undefined
                    )

                    onVisbilityChange?.(id, !isVisible, filteredLegendItemIds)

                    if (!onVisbilityChange) {
                      const serie = safeChartGet(`serie-${info.name}`)
                      if (serie) {
                        serie.setVisible(!serie.visible, true)
                        rerender((prev) => prev + 1)
                      }
                    }
                  }}
                >
                  <div>
                    <LegendItem
                      name={label}
                      color={currentColor}
                      serieType={settings.chart_type}
                      isActive={isVisible}
                    />
                  </div>
                </MenuItem>
              )
            })}
          </MenuButton>
        )}
      </div>
    </div>
  )
}

const getLabelForSerie = (serie: ChartSerie) => {
  return serie.info?.legendLabel ?? serie.info?.name ?? serie.name
}

export const Legend = memo(
  function Legend({
    chartSerieSettings,
    series,
    onVisibilityChange,
  }: LegendProps) {
    const offsetCounts = useMemo(() => {
      let i = 0
      let sum = 0
      const offsets = []
      for (const serie of chartSerieSettings.configs) {
        offsets[i++] = sum
        sum += series
          .filter((s) => s.id === serie.id)
          .filter((s) => getLabelForSerie(s) !== OTHER_LABEL)
          .slice(0, serie.limit ?? Infinity).length
      }

      return offsets
    }, [chartSerieSettings.configs, series])

    return (
      <Suspense fallback={<LegendPlaceholder />}>
        <div className="flex gap-6 items-start justify-start">
          {chartSerieSettings.configs.map((chartSetting, index) => {
            return (
              <Fragment key={index}>
                {index > 0 ? (
                  <Divider orientation="vertical" variant="secondary" />
                ) : null}
                <LegendRow
                  settings={chartSetting}
                  onVisbilityChange={onVisibilityChange}
                  offsetCount={offsetCounts?.[index] ?? 0}
                  series={series}
                />
              </Fragment>
            )
          })}
        </div>
      </Suspense>
    )
  },
  (a, b) => dequal(a, b)
)
